// Per-parameter trend chart shown above the reading list in
// ParamHistorySheet. Pure SVG, no charting library — matches the
// Sparkline pattern that's already in the codebase.
//
// Anti-pattern guardrails (see docs/ANTI-PATTERNS.md #3):
//   - Line colour is decided by the param's current status (ok/off),
//     not by trend direction. A falling-but-in-range value stays cyan.
//   - Trend direction is shown in muted grey above the chart, never as
//     a colour cue on the line itself.
//
// Layout (logical viewBox coords; CSS scales it):
//   - Target band drawn as a translucent cyan rectangle behind the line
//   - Y axis: 3 tick gridlines + labels on the left
//   - X axis: 3 date labels below the chart
//   - Outliers: alert-coloured ring around the dot
//   - Mouse: hover shows a vertical crosshair + nearest-point highlight +
//     HTML tooltip with the exact value + timestamp. Touch (most keepers
//     are on a phone, and a finger never hovers): a tap pins the tooltip,
//     sliding sideways scrubs it, a tap outside the chart clears it, and
//     the tooltip sits above the chart so the finger does not cover it.

import { useEffect, useRef, useState } from 'react';

// ViewBox dimensions chosen so the chart has room on a desktop
// without becoming a thin extruded line, while still scaling down
// cleanly on a phone (CSS width: 100% + aspect-ratio in theme.css).
const WIDTH = 840;
const FULL_HEIGHT = 320;
// The all-parameters page stacks one chart per parameter, so each is
// shorter and drops the trend line above it (the row header says it).
const COMPACT_HEIGHT = 170;
const PAD_TOP = 22;
const PAD_BOTTOM = 30;
const PAD_LEFT = 48;
const PAD_RIGHT = 16;

export function filterByRange(readings, days) {
  if (!days || days <= 0) return readings;
  const cutoff = Date.now() - days * 86400000;
  return readings.filter((r) => {
    const t = new Date(r.ts).getTime();
    return Number.isFinite(t) && t >= cutoff;
  });
}

// Compute the y-axis range over the data + the target band so the band
// is always visible even when readings are tightly clustered. 10% pad on
// both sides to avoid the line touching the chart edges.
export function computeRange(values, acceptable) {
  const all = values.filter(Number.isFinite).slice();
  if (Array.isArray(acceptable) && acceptable.length === 2) {
    const [lo, hi] = acceptable;
    if (Number.isFinite(lo)) all.push(lo);
    if (Number.isFinite(hi)) all.push(hi);
  }
  if (all.length === 0) return { min: 0, max: 1 };
  let min = Math.min(...all);
  let max = Math.max(...all);
  const span = max - min;
  const pad = span === 0 ? Math.max(Math.abs(max) * 0.1, 0.1) : span * 0.1;
  // A nitrate or phosphate axis padded to -0.19 reads as an impossible
  // value; the padding never crosses zero when nothing on the chart does.
  const floor = min >= 0 ? Math.max(0, min - pad) : min - pad;
  return { min: floor, max: max + pad };
}

export function pointsToPath(oldestFirst, geom) {
  if (!oldestFirst || oldestFirst.length === 0) return '';
  const { padLeft, padRight, padTop, padBottom, width, height, xMin, xMax, yMin, yMax } = geom;
  const innerW = width - padLeft - padRight;
  const innerH = height - padTop - padBottom;
  const xSpan = xMax - xMin || 1;
  const ySpan = yMax - yMin || 1;
  const xy = oldestFirst
    .map((r) => {
      const t = new Date(r.ts).getTime();
      const v = Number(r.value);
      if (!Number.isFinite(t) || !Number.isFinite(v)) return null;
      const x = padLeft + ((t - xMin) / xSpan) * innerW;
      const y = padTop + (1 - (v - yMin) / ySpan) * innerH;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .filter(Boolean);
  if (xy.length === 0) return '';
  return 'M' + xy.join(' L');
}

// `base`: where the scale starts. On S.G. that is 1.000, and 2% of 1.026
// (0.02, about 27 ppt) read every move as flat; on °F it is 32, or 78 to 77
// read flat while the same water in °C read falling.
export function describeTrend(oldestFirst, base = 0) {
  if (!oldestFirst || oldestFirst.length < 2) {
    return { direction: 'flat', delta: 0, arrow: '→' };
  }
  const first = Number(oldestFirst[0].value);
  const last = Number(oldestFirst[oldestFirst.length - 1].value);
  if (!Number.isFinite(first) || !Number.isFinite(last)) {
    return { direction: 'flat', delta: 0, arrow: '→' };
  }
  const delta = last - first;
  const threshold = Math.abs(first - base) * 0.02 || 0.01;
  if (Math.abs(delta) < threshold) return { direction: 'flat', delta, arrow: '→' };
  return {
    direction: delta > 0 ? 'rising' : 'falling',
    delta,
    arrow: delta > 0 ? '↑' : '↓',
  };
}

export function formatDelta(d, unit) {
  if (!Number.isFinite(d) || Math.abs(d) < 0.0001) return 'flat';
  const sign = d > 0 ? '+' : '';
  const abs = Math.abs(d);
  // A small S.G. move (0.0004) printed "+0.00".
  const formatted =
    abs < 0.01 ? d.toFixed(abs < 0.001 ? 4 : 3) : abs < 1 ? d.toFixed(2) : d.toFixed(1);
  const u = unit ? ` ${unit}` : '';
  return `${sign}${formatted}${u}`;
}

// `minDecimals`: the precision the page prints its values at (an S.G. band
// at four places), so the axis never reads coarser than the numbers.
export function formatTick(v, span = Infinity, minDecimals = 0) {
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  // A narrow range (S.G. 1.024-1.027) needs the decimals that tell its
  // ticks apart, or it reads "1.02 / 1.03 / 1.03".
  // An S.G. band of 0.0004 needs a fourth place, or every tick reads
  // "1.026"; the ticks sit half the span apart.
  if (span < 0.1) {
    const half = span / 2;
    const d = half > 0 ? Math.min(5, Math.max(3, Math.ceil(-Math.log10(half)))) : 3;
    return v.toFixed(Math.max(d, minDecimals || 0));
  }
  if (abs >= 100) return Math.round(v).toString();
  if (abs >= 10) return v.toFixed(1);
  if (abs >= 1) return v.toFixed(2);
  return v.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

function formatDate(ts, { withTime = false } = {}) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  if (withTime) {
    // Short windows (≤ 48 h) get the hour stamp on every x-axis tick;
    // a "6a", "noon", "8p" reads faster than "Mar 4" when the eye is
    // scanning a single-day chart.
    return d.toLocaleTimeString(undefined, { hour: 'numeric', hour12: true });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function ParamHistoryChart({ readings, param, status, rangeDays, compact = false }) {
  const HEIGHT = compact ? COMPACT_HEIGHT : FULL_HEIGHT;
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const tipRef = useRef(null);
  // null, or { ts, touch, inside }: the picked reading by its timestamp (a
  // live refresh or a new range must neither move the tooltip to another
  // reading nor drop one the keeper is still reading), touch for a pinned
  // finger or pen, inside when there is no room above the chart.
  const [picked, setPicked] = useState(null);
  // A finger that has touched down but not yet shown it is tapping or
  // scrubbing: a vertical flick through the page must not flash tooltips.
  const gestureRef = useRef(null);
  const pinned = !!picked?.touch;

  useEffect(() => {
    if (!pinned) return undefined;
    const onDown = (e) => {
      if (svgRef.current?.contains(e.target) || tipRef.current?.contains(e.target)) return;
      setPicked(null);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [pinned]);

  const filtered = filterByRange(readings || [], rangeDays);
  // The API serves readings newest-first; charts plot oldest → newest.
  const oldestFirst = [...filtered].reverse();

  if (oldestFirst.length < 2) {
    return (
      <div className="phc-empty">
        {oldestFirst.length === 0
          ? 'No readings in this range yet.'
          : 'Log a few more readings to see a trend.'}
      </div>
    );
  }

  // A median bucket's min and max widen the range, so its whisker fits.
  const values = oldestFirst
    .flatMap((r) => [Number(r.value), Number(r.min), Number(r.max)])
    .filter(Number.isFinite);
  const { min: yMin, max: yMax } = computeRange(values, param?.acceptable);
  const xMin = new Date(oldestFirst[0].ts).getTime();
  const xMax = new Date(oldestFirst[oldestFirst.length - 1].ts).getTime();

  const geom = {
    width: WIDTH,
    height: HEIGHT,
    padLeft: PAD_LEFT,
    padRight: PAD_RIGHT,
    padTop: PAD_TOP,
    padBottom: PAD_BOTTOM,
    xMin,
    xMax,
    yMin,
    yMax,
  };
  const path = pointsToPath(oldestFirst, geom);
  const stroke = status === 'off' ? 'var(--alert)' : 'var(--cyan)';
  const trend = describeTrend(
    oldestFirst,
    param?.unit === 'S.G.' ? 1 : param?.unit === '°F' ? 32 : 0
  );

  const innerW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const innerH = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const ySpan = yMax - yMin || 1;
  const xSpan = xMax - xMin || 1;
  const yToPx = (v) => PAD_TOP + (1 - (v - yMin) / ySpan) * innerH;
  const xToPx = (ts) => PAD_LEFT + ((ts - xMin) / xSpan) * innerW;

  let band = null;
  if (Array.isArray(param?.acceptable) && param.acceptable.length === 2) {
    const [lo, hi] = param.acceptable.map(Number);
    if (Number.isFinite(lo) && Number.isFinite(hi)) {
      const yHi = yToPx(hi);
      const yLo = yToPx(lo);
      band = { y: yHi, h: Math.max(0, yLo - yHi) };
    }
  }

  const yTicks = [yMin, (yMin + yMax) / 2, yMax];
  const outliers = oldestFirst.filter((r) => r.outlier_reason);
  // Build a closed fill polygon so we can shade under the line —
  // gives the chart depth without changing the data presentation.
  const fillPath =
    oldestFirst.length >= 2
      ? `${path} L ${xToPx(xMax)} ${HEIGHT - PAD_BOTTOM} L ${xToPx(xMin)} ${HEIGHT - PAD_BOTTOM} Z`
      : null;
  // "Latest" point gets a pulsing dot — at-a-glance "this is the
  // freshest read" affordance. Distinct from outlier rings.
  const latest = oldestFirst[oldestFirst.length - 1];
  const latestX = xToPx(new Date(latest.ts).getTime());
  const latestY = yToPx(Number(latest.value));
  // X-axis labels: 24 h windows show hour stamps, longer windows
  // show calendar dates. ~24 h is xSpan ≤ 30 h to cover the slop
  // when the user picks 24 h but the oldest reading is a few hours
  // past the cutoff.
  const shortWindow = xMax - xMin <= 30 * 3_600_000;
  const fmt = (ts) => formatDate(ts, { withTime: shortWindow });
  const gradId = `phc-fill-${(param?.key || 'x').replace(/[^a-z0-9]/gi, '')}`;

  // Pointer → nearest reading by chart X. We convert client coords
  // through the SVG's bounding rect to viewBox coords (the SVG scales,
  // so a clientX = 200 maps to a viewBox X that depends on render
  // width). Only X counts, so a finger anywhere above a reading picks it.
  const pick = (e) => {
    if (!e.isPrimary) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const vbX = ((e.clientX - rect.left) / rect.width) * WIDTH;
    // Find the reading whose xToPx is closest to vbX.
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < oldestFirst.length; i++) {
      const d = Math.abs(xToPx(new Date(oldestFirst[i].ts).getTime()) - vbX);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    const ts = oldestFirst[bestIdx].ts;
    const touch = e.pointerType !== 'mouse';
    // Above the chart is clear of the hand, unless the chart sits under
    // the top of the screen (the app header floats there).
    const inside = touch && rect.top < 170;
    setPicked((prev) =>
      prev && prev.ts === ts && prev.touch === touch && prev.inside === inside
        ? prev
        : { ts, touch, inside }
    );
  };
  const onPointerDown = (e) => {
    if (!e.isPrimary) return;
    if (e.pointerType === 'mouse') {
      pick(e);
      return;
    }
    gestureRef.current = { x: e.clientX, y: e.clientY, scrubbing: false };
  };
  const onPointerMove = (e) => {
    if (e.pointerType === 'mouse') {
      pick(e);
      return;
    }
    const g = gestureRef.current;
    if (!g || !e.isPrimary) return;
    if (!g.scrubbing) {
      const dx = Math.abs(e.clientX - g.x);
      const dy = Math.abs(e.clientY - g.y);
      if (dx > 8 && dx > dy) g.scrubbing = true;
      else return;
    }
    pick(e);
  };
  const onPointerUp = (e) => {
    const g = gestureRef.current;
    gestureRef.current = null;
    if (!g || g.scrubbing || !e.isPrimary) return;
    if (Math.hypot(e.clientX - g.x, e.clientY - g.y) < 10) pick(e);
  };
  // A finger or pen lifting fires pointerleave too; only a mouse leaving
  // clears.
  const onPointerLeave = (e) => {
    if (e.pointerType === 'mouse') setPicked(null);
  };
  // The browser took the gesture for a vertical page scroll (touch-action
  // pan-y): the keeper was scrolling past the chart, not reading it.
  const onPointerCancel = () => {
    gestureRef.current = null;
    setPicked(null);
  };

  const hover = picked ? oldestFirst.find((r) => r.ts === picked.ts) || null : null;
  const hoverX = hover ? xToPx(new Date(hover.ts).getTime()) : null;
  const hoverY = hover ? yToPx(Number(hover.value)) : null;
  // Tooltip position in viewBox space; flip horizontally on the right
  // half of the chart so it stays inside the wrapper.
  const tipLeftPct = hoverX != null ? (hoverX / WIDTH) * 100 : 0;
  const tipFlipRight = tipLeftPct > 65;

  return (
    <div className={compact ? 'phc phc-compact' : 'phc'} ref={wrapRef}>
      {!compact && (
        <div className="phc-meta">
          <span className="phc-trend" data-direction={trend.direction}>
            {trend.arrow} <span className="phc-delta">{formatDelta(trend.delta, param?.unit)}</span>{' '}
            <span className="phc-trend-context">
              over {oldestFirst.length} reading{oldestFirst.length === 1 ? '' : 's'}
            </span>
          </span>
          {param?.target != null && (
            <span className="phc-target">
              target {param.target}
              {param.unit ? ` ${param.unit}` : ''}
            </span>
          )}
        </div>
      )}

      <div className="phc-stage">
        <svg
          ref={svgRef}
          className="phc-svg"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${param?.label || 'Parameter'} history chart`}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onPointerCancel={onPointerCancel}
        >
          <defs>
            <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity="0.32" />
              <stop offset="100%" stopColor={stroke} stopOpacity="0" />
            </linearGradient>
          </defs>
          {band && (
            <rect
              x={PAD_LEFT}
              y={band.y}
              width={innerW}
              height={band.h}
              fill="rgba(125, 211, 255, 0.10)"
            />
          )}
          {yTicks.map((v, i) => (
            <g key={`y-${i}`}>
              <line
                x1={PAD_LEFT}
                x2={WIDTH - PAD_RIGHT}
                y1={yToPx(v)}
                y2={yToPx(v)}
                stroke="rgba(168, 192, 224, 0.10)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={PAD_LEFT - 8}
                y={yToPx(v) + 4}
                textAnchor="end"
                className="phc-tick"
                fill="var(--text-dim)"
              >
                {formatTick(v, yMax - yMin, param?.decimals)}
              </text>
            </g>
          ))}
          {fillPath && <path d={fillPath} fill={`url(#${gradId})`} />}
          {/* A median hides a spike (a heater stuck on for three hours), and
              a phone has no hover to show the spread: each bucket draws its
              min to max, in alert red when it leaves the range. */}
          {oldestFirst.map((r, i) => {
            const lo = Number(r.min);
            const hi = Number(r.max);
            if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;
            const [bLo, bHi] = Array.isArray(param?.acceptable)
              ? param.acceptable.map(Number)
              : [-Infinity, Infinity];
            const out = lo < bLo || hi > bHi;
            const x = xToPx(new Date(r.ts).getTime());
            return (
              <line
                key={`w-${i}`}
                x1={x}
                x2={x}
                y1={yToPx(hi)}
                y2={yToPx(lo)}
                stroke={out ? 'var(--alert)' : stroke}
                strokeOpacity={out ? 0.8 : 0.3}
                strokeWidth="2"
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          <path
            d={path}
            fill="none"
            stroke={stroke}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {outliers.map((r) => (
            <circle
              key={r.id}
              cx={xToPx(new Date(r.ts).getTime())}
              cy={yToPx(Number(r.value))}
              r="5.5"
              fill="none"
              stroke="var(--alert)"
              strokeWidth="1.8"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* Latest-reading pulse — eye lands on "right now" when the
              chart updates. */}
          <circle cx={latestX} cy={latestY} r="5" fill={stroke} className="phc-latest-dot" />
          <circle
            cx={latestX}
            cy={latestY}
            r="5"
            fill="none"
            stroke={stroke}
            strokeWidth="2"
            className="phc-latest-pulse"
            vectorEffect="non-scaling-stroke"
          />
          {/* Hover crosshair + nearest-point highlight. Rendered last
              so it sits above the line + dots. */}
          {hover && (
            <>
              <line
                x1={hoverX}
                x2={hoverX}
                y1={PAD_TOP}
                y2={HEIGHT - PAD_BOTTOM}
                stroke="var(--cyan)"
                strokeWidth="1"
                strokeDasharray="4 4"
                opacity="0.6"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
              <circle
                cx={hoverX}
                cy={hoverY}
                r="8"
                fill="var(--sheet-bg)"
                stroke={stroke}
                strokeWidth="2.5"
                vectorEffect="non-scaling-stroke"
                pointerEvents="none"
              />
            </>
          )}
        </svg>

        {hover &&
          (() => {
            const tipBody = (
              <>
                <div className="phc-tooltip-value">
                  {hover.value}
                  <span className="phc-tooltip-unit">{hover.unit || param?.unit || ''}</span>
                </div>
                <div className="phc-tooltip-time">
                  {hover.n > 1
                    ? formatDate(new Date(hover.ts).getTime(), { withTime: shortWindow })
                    : `${formatDate(new Date(hover.ts).getTime(), { withTime: shortWindow })} · ${new Date(
                        hover.ts
                      ).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`}
                </div>
                {/* A dense probe series comes back as one median per hour, day
                    or week; say so, and give the spread it hides. */}
                {hover.n > 1 && (
                  <div className="phc-tooltip-time">
                    middle of {hover.n} readings
                    {Number.isFinite(hover.min) && Number.isFinite(hover.max)
                      ? ` (${hover.min}–${hover.max})`
                      : ''}
                  </div>
                )}
                {hover.method && (
                  <div className={`phc-tooltip-source phc-tooltip-source-${hover.method}`}>
                    {hover.method}
                  </div>
                )}
              </>
            );
            if (!picked?.touch) {
              return (
                <div
                  className={`phc-tooltip ${tipFlipRight ? 'phc-tooltip-right' : ''}`}
                  style={
                    tipFlipRight ? { right: `${100 - tipLeftPct}%` } : { left: `${tipLeftPct}%` }
                  }
                >
                  {tipBody}
                </div>
              );
            }
            // Under a finger: above the chart, or, with no room there, inside
            // it on the half away from the finger. A tap on it closes it, and
            // lands on it rather than on the card header underneath.
            let place;
            if (!picked.inside) {
              place = tipFlipRight ? { right: `${100 - tipLeftPct}%` } : { left: `${tipLeftPct}%` };
            } else {
              place = tipLeftPct < 50 ? { right: '4px' } : { left: `${(PAD_LEFT / WIDTH) * 100}%` };
            }
            return (
              <button
                type="button"
                ref={tipRef}
                className={`phc-tooltip phc-tooltip-touch ${
                  picked.inside
                    ? 'phc-tooltip-inside'
                    : `phc-tooltip-above ${tipFlipRight ? 'phc-tooltip-right' : ''}`
                }`}
                style={place}
                onClick={() => setPicked(null)}
              >
                {tipBody}
              </button>
            );
          })()}
      </div>

      <div className="phc-x-labels">
        <span>{fmt(xMin)}</span>
        <span>{fmt((xMin + xMax) / 2)}</span>
        <span>{fmt(xMax)}</span>
      </div>
    </div>
  );
}
